import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Truck,
  Package,
  Search,
  Filter,
  ChevronDown,
  RefreshCw,
  Box
} from 'lucide-react';
import { VanReceptionsInbox } from '../components/VanReceptionsInbox';

// A "carrinha" is a warehouse explicitly classified as `warehouse_type === 'carrinha'`
// by the backend. The classification is deterministic (PT plate pattern) and
// admin-editable in `/warehouses`. NB: this no longer depends on naming heuristics.
const isVan = (w) => w && w.warehouse_type === 'carrinha';

export default function MyVanStock() {
  const { user } = useContext(AuthContext);

  const [allWarehouses, setAllWarehouses] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hasStockOnly, setHasStockOnly] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 30;

  // Decide which van the user is viewing.
  // - admin / PM-view-all: any van they choose; default = own reference_warehouse if it's a van, else first van.
  // - others: locked to their reference_warehouse.
  const isAdmin = user?.role === 'admin';
  const isPMViewAll = user?.role === 'procurement_manager' && user?.view_all_warehouses_enabled;
  const canChooseVan = isAdmin || isPMViewAll;
  
  // Get user's reference warehouse (canonical) with legacy fallback
  const userReferenceWarehouse = user?.reference_warehouse || user?.default_warehouse;

  // Fetch warehouses once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/warehouses`);
        setAllWarehouses(res.data.warehouses || []);
      } catch (e) { /* fail silent — empty list will show */ }
    })();
  }, []);

  // List of vans the user is allowed to see, ordered with own first.
  const visibleVans = useMemo(() => {
    const effective = new Set(user?.effective_warehouses || []);
    let vans = allWarehouses
      .filter(isVan)
      .filter((w) => canChooseVan || effective.has(w.code));
    // Sort: own reference warehouse first, then alpha
    vans = vans.sort((a, b) => {
      if (a.code === userReferenceWarehouse) return -1;
      if (b.code === userReferenceWarehouse) return 1;
      return a.code.localeCompare(b.code);
    });
    return vans;
  }, [allWarehouses, user, canChooseVan, userReferenceWarehouse]);

  // Pick the default selected warehouse once vans are loaded.
  useEffect(() => {
    if (selectedCode) return;
    if (!visibleVans.length) return;
    const ownVan = visibleVans.find((w) => w.code === userReferenceWarehouse);
    setSelectedCode(ownVan ? ownVan.code : visibleVans[0].code);
  }, [visibleVans, userReferenceWarehouse, selectedCode]);

  const warehouseInfo = useMemo(
    () => allWarehouses.find((w) => w.code === selectedCode) || null,
    [allWarehouses, selectedCode],
  );

  // Fetch products
  const fetchProducts = useCallback(async () => {
    if (!selectedCode) { setLoading(false); return; }
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize,
        sort_by: 'sku',
        sort_order: 'asc',
      };
      if (hasStockOnly) params.has_stock = true;
      if (search.trim()) params.search = search.trim();

      const res = await axios.get(`${API}/warehouses/${selectedCode}/products`, { params });
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Error fetching van stock:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedCode, page, search, hasStockOnly]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.ceil(total / pageSize);
  const stockCount = products.filter(p => (p.warehouse_stock || 0) > 0).length;

  if (!selectedCode) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" data-testid="my-van-stock-empty">
        <Truck className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-lg font-bold text-slate-700">
          {canChooseVan ? 'Sem carrinhas disponíveis' : 'Sem Carrinha Atribuída'}
        </h2>
        <p className="text-sm text-slate-500 mt-2 max-w-xs">
          {canChooseVan
            ? 'Nenhuma carrinha foi sincronizada do SAGE ainda.'
            : 'Peça ao administrador para configurar a sua carrinha por defeito na página de Utilizadores.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh]" data-testid="my-van-stock-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
              <Truck className="h-5 w-5 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              {canChooseVan && visibleVans.length > 1 ? (
                <Select value={selectedCode} onValueChange={(v) => { setSelectedCode(v); setPage(1); }}>
                  <SelectTrigger
                    className="border-0 px-0 h-auto py-0 hover:bg-transparent shadow-none font-bold text-base text-slate-900 max-w-full"
                    data-testid="van-selector"
                  >
                    <SelectValue placeholder="Escolher carrinha" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {visibleVans.map((w) => (
                      <SelectItem key={w.code} value={w.code} data-testid={`van-option-${w.code}`}>
                        <div className="flex flex-col">
                          <span className="font-medium">{w.name || w.code}</span>
                          {w.name !== w.code && (
                            <span className="text-xs text-muted-foreground font-mono">{w.code}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <h1 className="text-base font-bold text-slate-900 truncate" data-testid="van-name">
                  {warehouseInfo?.name || selectedCode}
                </h1>
              )}
              <p className="text-xs text-slate-500">
                {selectedCode}
                {canChooseVan && visibleVans.length > 1 && (
                  <span className="ml-2 text-blue-600">· {visibleVans.length} carrinhas disponíveis</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={fetchProducts}
            className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-400 active:bg-slate-100 flex-shrink-0"
            data-testid="refresh-stock"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats - Inline metrics, no heavy boxes */}
        <div className="px-4 pb-3 flex gap-6 border-b border-slate-100">
          <div>
            <p className="text-2xl font-mono tabular-nums font-semibold text-slate-900">{total}</p>
            <p className="text-xs text-slate-500">Referências</p>
          </div>
          <div>
            <p className="text-2xl font-mono tabular-nums font-semibold text-slate-900">
              {products.reduce((sum, p) => sum + (p.available !== undefined ? p.available : (p.warehouse_stock || 0)), 0)}
            </p>
            <p className="text-xs text-slate-500">Unidades</p>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="px-4 py-3 flex gap-2 bg-slate-50/50">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 h-10 text-sm bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => { setHasStockOnly(!hasStockOnly); setPage(1); }}
            className={`h-10 px-3 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
              hasStockOnly
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-600'
            }`}
            data-testid="stock-filter-toggle"
          >
            {hasStockOnly ? 'Com Stock' : 'Todos'}
          </button>
        </div>
      </div>

      {/* Pending receptions (TAR replenishments awaiting confirmation) */}
      <VanReceptionsInbox warehouseCode={selectedCode} onConfirmed={fetchProducts} />

      {/* Product List */}
      <div className="flex-1 overflow-y-auto pb-20">
        {loading && products.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-8 w-8 animate-spin text-slate-300" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">
              {search ? 'Nenhum resultado encontrado' : hasStockOnly ? 'Sem artigos em stock' : 'Sem artigos neste armazém'}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100">
              {products.map((product) => {
                const whStock = product.warehouse_stock || 0;
                const reserved = product.reserved || 0;
                const available = product.available !== undefined ? product.available : whStock;
                const totalStock = product.current_stock || 0;
                return (
                  <div
                    key={product.id || product.sku}
                    className="px-4 py-3 border-b border-slate-100 last:border-b-0"
                    data-testid={`product-row-${product.sku}`}
                  >
                    {/* Row 1: Name + Available Stock */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 leading-snug">
                          {product.name}
                        </p>
                        <p className="text-xs text-slate-400 font-mono mt-0.5">{product.sku}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className={`text-xl font-mono tabular-nums font-semibold ${
                          available > 0 ? 'text-slate-900' : 'text-slate-300'
                        }`}>
                          {available}
                        </span>
                        {reserved > 0 && (
                          <span className="text-xs text-amber-600 block">
                            {reserved} cativo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-4 pb-2 px-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium disabled:opacity-30 active:bg-slate-50"
                >
                  Anterior
                </button>
                <span className="text-sm text-slate-500">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium disabled:opacity-30 active:bg-slate-50"
                >
                  Seguinte
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
